import { useState } from "react";
import { Badge, Button, Card, Divider, Group, Skeleton, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { CashbackStatus } from "@safiri/shared";
import { cashbackApi } from "../../api/cashback";
import { usePermission } from "../../auth/usePermission";
import { formatDateTime, formatMoney } from "../../lib/format";
import { ReverseModal } from "./CashbackPanel";

function Row({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Stack gap={0} align="flex-end">
        <Text size="sm" fw={500}>{value}</Text>
        {sub && <Text size="xs" c="dimmed">{sub}</Text>}
      </Stack>
    </Group>
  );
}

/** Trip detail → Cashback line: amounts, rates used, status (spec §7). */
export function TripCashbackCard({ tripId, completed }: { tripId: string; completed: boolean }) {
  const canView = usePermission("finance.view");
  const canManage = usePermission("finance.manage");
  const [reversing, setReversing] = useState(false);

  const q = useQuery({
    queryKey: ["cashback-trip", tripId],
    queryFn: () => cashbackApi.forTrip(tripId),
    enabled: canView && completed,
  });

  if (!canView || !completed) return null;
  const r = q.data;

  return (
    <Card withBorder radius="md" padding="lg" mt="md">
      <Group justify="space-between" mb="md">
        <Title order={5}>Cashback</Title>
        {r && (
          <Badge color={r.status === CashbackStatus.REVERSED ? "red" : "green"} variant="light">
            {r.status === CashbackStatus.REVERSED ? "Reversed" : "Granted"}
          </Badge>
        )}
      </Group>
      {q.isLoading ? (
        <Skeleton h={80} />
      ) : !r ? (
        <Text size="sm" c="dimmed">No cashback on this ride (program off, or nothing earned).</Text>
      ) : (
        <Stack gap="xs">
          <Row label="Fare base" value={formatMoney(r.fareBase)} />
          <Divider />
          <Row label={`Passenger (${r.passengerPercentage}%)`} value={formatMoney(r.passengerAmount)} />
          <Row
            label={`Driver (${r.driverPercentage}%)`}
            value={formatMoney(r.driverAmount)}
            sub={
              r.driverCommissionOffset > 0
                ? `${formatMoney(r.driverCommissionOffset)} paid commission, ${formatMoney(r.driverWithdrawable)} withdrawable`
                : undefined
            }
          />
          <Text size="xs" c="dimmed">
            Granted {formatDateTime(r.grantedAt)}
            {r.source === "reconcile" ? " by the scheduled check" : ""}
          </Text>
          {r.reversal && (
            <>
              <Divider />
              <Text size="sm">
                Reversed {formatDateTime(r.reversal.at)} by {r.reversal.byName ?? "—"}: {r.reversal.reason}
              </Text>
              <Row label="Taken from passenger" value={formatMoney(r.reversal.passengerRecovered)} />
              {r.reversal.passengerWrittenOff > 0 && (
                <Row label="Written off (already spent)" value={formatMoney(r.reversal.passengerWrittenOff)} />
              )}
              <Row label="Taken from driver" value={formatMoney(r.reversal.driverRecovered)} />
            </>
          )}
          {canManage && r.status === CashbackStatus.GRANTED && (
            <Button size="xs" variant="light" color="red" mt="xs" onClick={() => setReversing(true)}>
              Reverse cashback
            </Button>
          )}
        </Stack>
      )}
      <ReverseModal record={reversing && r ? r : null} onClose={() => setReversing(false)} />
    </Card>
  );
}
